import consola from 'consola';
import { ca } from '@/lib/channelAttribution.js';
import { validateJWTs } from '~/lib/validation';
import { getCommonTracking } from '~/tracking/common';

export const useSessionStore = defineStore('sessionStore', () => {
  const {
    isTablet,
    isDesktop,
    userAgent,
  } = useDevice();

  // Define the default/initial shape here
  const getDefaultState = () => ({
    entryPoint: '',
    device: isTablet ? 'tablet' : isDesktop ? 'desktop' : 'mobile',
    userAgent,
    auth: false,
    user: {
      customerId: 0,
      email: '',
      name: '',
      nationality: {
        label: '',
        value: '',
      },
      picture: '',
      accountOrigin: 'standard',
      isNameGenerated: false,
      loginCount: -1,
      idToken: null,
      apiToken: {
        accessToken: null,
        refreshToken: null,
        expiresIn: null,
      },
      optOutSocialFeatures: false,
      acceptSocialTerms: false,
      birthday: '',
      newsletterOptin: false,
    },
    placeholderEmail: '', // TODO: this may be refactored/removed after the Iterable integration
    language: {
      code: 'en_GB',
      tag: 'en',
      lang: 'en',
      url: 'en',
      baseUrl: 'BASE_URL_EN',
      name: 'English',
      enName: 'English',
    },
    currency: {
      code: 'EUR',
      transcode: 't_EUR',
      symbol: '€',
    },
  });

  const state = ref(getDefaultState());

  const $auth0 = ref(null);
  const $jwks = ref(null);
  const $jwt = ref(null);

  const resetSessionStore = () => {
    state.value = getDefaultState();
  };

  const getEntryPoint = computed(() => {
    return state?.value?.entryPoint;
  });

  const getEntryPointForTracking = computed(() => {
    switch (getEntryPoint.value) {
      case 'HOMEPAGE':
        return 'homepage';
      case 'HOSTEL':
      case 'HOTEL':
      case 'GUESTHOUSE':
        return 'propertyLanding';
      case 'CONTINENT':
        return 'continentLanding';
      case 'COUNTRY':
        return 'countryLanding';
      case 'AREA':
      case 'REGION':
        return 'regionLanding';
      case 'CITY':
      case 'DISTRICT':
        return 'staticFab';
      case 'PROPERTY':
        return 'staticMicrosite';
      case 'EVENTSWORLDWIDE':
        return 'eventsWorldwide';
      case 'EVENTSCONTINENT':
        return 'eventsContinent';
      case 'EVENTSCOUNTRY':
        return 'eventsCountry';
      case 'EVENTSCITY':
        return 'eventsCity';
      case 'EVENTDETAILS':
        return 'eventDetails';
      default:
        return 'other';
    }
  });

  const getUserDevice = computed(() => {
    return state?.value?.device;
  });

  const getUserUA = computed(() => {
    return state?.value?.userAgent;
  });

  const setEntryPoint = (value) => {
    if (!state.value) {
      resetSessionStore();
    }
    state.value.entryPoint = value;
  };

  const setAuth = ({
    isAuthenticated,
    userId,
  }) => {
    if (!state.value) {
      resetSessionStore();
    }

    state.value.auth = isAuthenticated;
    state.value.user.customerId = Number(userId) || 0;
  };

  const setRedirectUrl = (myWorldRedirect, accountSettings, url) => {
    const { useUrl } = useUrls();

    if (myWorldRedirect) {
      localStorage.setItem('redirect-url', `${useUrl.getDynamicBaseURL()}account/trips`);
      return;
    }

    if (accountSettings) {
      localStorage.setItem('redirect-url', `${useUrl.getDynamicBaseURL()}account/details`);
      return;
    }

    localStorage.setItem('redirect-url', typeof url === 'string' ? url : url.href);
  };

  const clearRedirectUrl = () => {
    state.value.redirectUrl = '';
  };

  const generateCsrfPreventionState = () => {
    const runtimeConfig = useRuntimeConfig();
    const domain = runtimeConfig.public.COOKIE_SESSION_DOMAIN;
    const csrfCookieName = runtimeConfig.public.LOGIN_CSRF_PROTECTION_COOKIE;

    const key = crypto.randomUUID();

    const csrfCookie = useCookie(csrfCookieName, {
      path: '/',
      secure: true,
      domain,
      sameSite: 'Lax',
      maxAge: 60 * 10,
    });

    csrfCookie.value = key;

    return key;
  };

  const getAuthorizePayload = ({
    redirectUrl,
    gtmOptions,
    email,
  }) => {
    const nuxtApp = useNuxtApp();
    const { $tracking } = nuxtApp;
    const { locale } = nuxtApp.$i18n;

    const gaCookie = useCookie('_ga')?.value || '';

    const currentLanguageTag = locale?.value;

    // Works only clientside. Please don't call serverside
    const segmentOptions = $tracking?.getSegmentOptions; // TODO: Not all of the Segment Options are implemented

    const payload = {
      redirectUri: redirectUrl(),
      responseType: 'code',
      ui_locales: currentLanguageTag,
      gtmOptions,
      scope: 'profile email openid offline_access',
      platform: 'web',
      ga: gaCookie,
      state: generateCsrfPreventionState(),
      featureToggles: {}, // socialLoginVariant2: inAppLogin,
      segmentOptions,
    };

    if (email) {
      payload.login_hint = email;
      payload.connection = 'google-oauth2';
    }

    return payload;
  };

  const setAuth0 = (auth0) => {
    $auth0.value = auth0;
  };

  const getAuth0 = () => {
    return $auth0.value;
  };

  const setJwks = (jwks) => {
    $jwks.value = jwks;
  };

  const getJwks = () => {
    return $jwks.value;
  };

  const setJwt = (jwt) => {
    $jwt.value = jwt;
  };

  const getJwt = () => {
    return $jwt.value;
  };

  const getSessionUser = () => {
    return state.value.user;
  };

  const setIdToken = (idToken) => {
    if (!state.value) {
      resetSessionStore();
    }
    state.value.user.idToken = idToken || null;
  };

  const getAuth = () => {
    return state.value.auth;
  };

  const getCustomerId = () => {
    return state.value?.user?.customerId;
  };

  const getAccessToken = () => {
    return state.value?.user?.apiToken?.accessToken;
  };

  const setPlaceholderEmail = (placeholderEmail) => {
    if (!state.value) {
      resetSessionStore();
    }
    state.value.placeholderEmail = placeholderEmail;
  };

  const getPlaceholderEmail = () => {
    return state.value.placeholderEmail;
  };

  const setProfilePicture = (pictureUrl) => {
    state.value.user.picture = pictureUrl || '';
  };

  const setLoginCount = (count) => {
    state.value.user.loginCount = Number(count);
  };

  const setAccountOrigin = (origin) => {
    if (origin.toLowerCase().includes('google')) {
      state.value.user.accountOrigin = 'Google';
    } else if (origin.toLowerCase().includes('facebook')) {
      state.value.user.accountOrigin = 'Facebook';
    } else if (origin.toLowerCase().includes('apple')) {
      state.value.user.accountOrigin = 'Apple';
    }
  };

  const setIsNameGenerated = (isNameGenerated) => {
    state.value.user.isNameGenerated = isNameGenerated;
  };

  const setEmail = (email) => {
    state.value.user.email = email;
  };

  const setName = (name) => {
    state.value.user.name = name;
  };

  const setBirthday = (birthday) => {
    state.value.user.birthday = birthday;
  };

  const setNationality = (nationality) => {
    state.value.user.nationality = {
      value: nationality?.code,
      label: nationality?.name,
    };
  };

  const setAcceptSocialTerms = (socialTerms) => {
    state.value.user.acceptSocialTerms = socialTerms;
  };

  const setOptOutSocialTerms = (optOutSocialFeatures) => {
    state.value.user.optOutSocialFeatures = optOutSocialFeatures;
  };

  const setNewsletterOptin = (hasNewsletter) => {
    state.value.user.newsletterOptin = hasNewsletter;
  };

  const getNewsletterOptin = computed(() => {
    return state.value.user.newsletterOptin;
  });

  const setApiToken = (apiToken) => {
    state.value.user.apiToken.accessToken = apiToken.accessToken || null;
    state.value.user.apiToken.refreshToken = apiToken.refreshToken || null;
    state.value.user.apiToken.expiresIn = apiToken.expiresIn || null;
  };

  const auth0Login = (myWorldRedirect, accountSettings, email) => {
    if (process.server) {
      return;
    }

    const nuxtApp = useNuxtApp();

    const runtimeConfig = useRuntimeConfig();
    const { getGtmOptions } = getCommonTracking(nuxtApp);
    const { useUrl } = useUrls();

    const lastChannelAttributionUrl
      = ca?.getLastChannelAttributionToQuery(runtimeConfig.public.DEFAULT_BASE_URL) || null;
    setRedirectUrl(myWorldRedirect, accountSettings, lastChannelAttributionUrl);

    const options = getAuthorizePayload({
      redirectUrl: useUrl.getAuth0CallbackPageUrl,
      gtmOptions: getGtmOptions,
      email,
    });

    const auth0 = $auth0.value;

    auth0.authorize(options, (err) => {
      if (err) {
        consola.error(`### Unable to login with auth0. ${err}`);
        // No redirect in DEV / TEST
        if (runtimeConfig.public.RELEASE_ENV === 'production' || runtimeConfig.public.RELEASE_ENV === 'staging') {
          window.location.assign(`${useUrl.getStaticBaseURL()}404`);
        }
      }
    });

    sessionStorage.setItem('isLogin', 'true');
  };

  const setSessionCookies = (session) => {
    const {
      getLoginCookieOptions,
      getRefreshCookieOptions,
      clearLoginCookies,
      LOGIN_TOKEN_COOKIE,
      LOGIN_ACCESS_TOKEN_COOKIE,
      LOGIN_REFRESH_TOKEN_COOKIE,
      LOGIN_EXPIRES_IN_COOKIE,
    } = useLoginCookies();

    if (!session || (!session.idToken && !session.apiToken)) {
      clearLoginCookies();
      return;
    }

    const idTokenMaxAge = 8 * 60 * 60;
    const accessTokenMaxAge = Number.parseInt(session?.apiToken?.expiresIn) || 3600;
    const refreshTokenMaxAge = 8640000;

    const userLoginIdToken = useCookie(LOGIN_TOKEN_COOKIE, { ...getLoginCookieOptions(idTokenMaxAge) });
    const userLoginAccessToken = useCookie(LOGIN_ACCESS_TOKEN_COOKIE, { ...getLoginCookieOptions(accessTokenMaxAge) });
    const userLoginExpiresInToken = useCookie(LOGIN_EXPIRES_IN_COOKIE, { ...getLoginCookieOptions(accessTokenMaxAge) });

    userLoginIdToken.value = session?.idToken || null;
    userLoginAccessToken.value = session?.apiToken?.accessToken || null;
    userLoginExpiresInToken.value = session?.apiToken?.expiresIn || null;

    // Refresh is HttpOnly and must only be written on the server (FBF-982)
    if (false && session?.apiToken?.refreshToken) {
      const userLoginRefreshToken = useCookie(
        LOGIN_REFRESH_TOKEN_COOKIE,
        { ...getRefreshCookieOptions(refreshTokenMaxAge) },
      );
      userLoginRefreshToken.value = session.apiToken.refreshToken;
    }
  };

  const clearSession = () => {
    resetSessionStore();
    // Note: Cookie clearing should be handled by the caller (plugin/middleware/component)
    // to ensure composables are called in the correct context
  };

  const setSession = async (session) => {
    if (!(session?.idToken && session?.apiToken?.accessToken)) {
      throw new Error('Missing ID Token or Access Token.');
    }

    // Only validate JWTs on server side
    if (process.server && getJwt() && getJwks()) {
      await validateJWTs([session.idToken, session.apiToken.accessToken], getJwt(), getJwks());
    }

    setIdToken(session.idToken);
    setApiToken(session.apiToken);

    if (!getJwt() || !$jwt.value) {
      throw new Error('JWT decoder not available.');
    }

    const decodedIdToken = $jwt.value.decode(session.idToken);

    if (!decodedIdToken) {
      throw new Error('Invalid ID Token.');
    }

    setAuth({
      isAuthenticated: true,
      userId: decodedIdToken['https://hostelworld.com/hw_id'],
    });
    setProfilePicture(decodedIdToken['https://hostelworld.com/uploaded_picture']);
    setLoginCount(decodedIdToken['https://hostelworld.com/loginCount']);
    setAccountOrigin(decodedIdToken['https://hostelworld.com/connection_type']);
    setIsNameGenerated(decodedIdToken['https://hostelworld.com/is_jwt_name_generated']);
    setEmail(decodedIdToken.email);
    setName(
      decodedIdToken.given_name ? `${decodedIdToken.given_name} ${decodedIdToken.family_name}` : decodedIdToken.name,
    );
    setAcceptSocialTerms(decodedIdToken['https://hostelworld.com/acceptSocialTCs']);
    setOptOutSocialTerms(decodedIdToken['https://hostelworld.com/optOutSocialFeatures']);
    setNewsletterOptin(decodedIdToken['https://hostelworld.com/newsletter']);
  };

  const REFRESH_TOKEN_FETCH_TIMEOUT_MS = 5000;

  const refreshSession = async (refreshToken, appConfigPublic) => {
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: appConfigPublic.AUTH0_CLIENTID,
      refresh_token: refreshToken,
    });

    const url = `https://${appConfigPublic.AUTH0_DOMAIN}/oauth/token`;

    const {
      id_token,
      access_token,
      refresh_token,
      expires_in,
    } = await $fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body,
      timeout: REFRESH_TOKEN_FETCH_TIMEOUT_MS,
    });

    if (!(id_token && access_token)) {
      throw new Error('Token refresh response missing id_token or access_token.');
    }

    // Auth0 omits refresh_token when rotation is off; keep the one we used.
    return {
      userLoginIdToken: id_token,
      userLoginAccessToken: access_token,
      userLoginRefreshToken: refresh_token || refreshToken,
      userLoginExpiresInToken: expires_in,
    };
  };

  /**
   * Lightweight session initialization - only decodes ID token for display purposes
   * No JWT validation, no token refresh, just basic user info
   * Used for pages that don't require real-time session validation
   */
  const initializeLightweightSession = (idToken) => {
    if (!idToken) {
      return;
    }

    try {
      if (!getJwt() || !$jwt.value) {
        // JWT decoder not available, skip lightweight init
        return;
      }

      const decodedIdToken = $jwt.value.decode(idToken);
      if (!decodedIdToken) {
        return;
      }

      // Set all available user information from decoded token
      // This is safe because we're only using it for display, not for authentication
      // The expensive operations (JWT validation, token refresh) are skipped
      setAuth({
        isAuthenticated: true,
        userId: decodedIdToken['https://hostelworld.com/hw_id'],
      });
      setProfilePicture(decodedIdToken['https://hostelworld.com/uploaded_picture']);
      setLoginCount(decodedIdToken['https://hostelworld.com/loginCount']);
      setAccountOrigin(decodedIdToken['https://hostelworld.com/connection_type']);
      setIsNameGenerated(decodedIdToken['https://hostelworld.com/is_jwt_name_generated']);
      setEmail(decodedIdToken.email);
      setName(
        decodedIdToken.given_name ? `${decodedIdToken.given_name} ${decodedIdToken.family_name}` : decodedIdToken.name,
      );
      setAcceptSocialTerms(decodedIdToken['https://hostelworld.com/acceptSocialTCs']);
      setOptOutSocialTerms(decodedIdToken['https://hostelworld.com/optOutSocialFeatures']);
      setNewsletterOptin(decodedIdToken['https://hostelworld.com/newsletter']);

      // Set ID token in store (for client-side navigation)
      setIdToken(idToken);

      // Note: We don't validate or refresh tokens here, saving performance
      // The full validation will happen when needed (e.g., on account pages)
    } catch (error) {
      consola.error(`### Error initializing lightweight session: ${error?.message}`);
    }
  };

  // State is already initialized above

  return {
    state,
    resetSessionStore,
    getEntryPoint,
    getEntryPointForTracking,
    getUserDevice,
    getUserUA,
    setEntryPoint,
    setRedirectUrl,
    clearRedirectUrl,
    auth0Login,
    setAuth0,
    getAuth0,
    setJwks,
    setJwt,
    getJwks,
    getJwt,
    getSessionUser,
    setSession,
    setIdToken,
    setApiToken,
    setAuth,
    getAuth,
    getCustomerId,
    getAccessToken,
    setPlaceholderEmail,
    getPlaceholderEmail,
    setProfilePicture,
    setLoginCount,
    setAccountOrigin,
    setIsNameGenerated,
    setEmail,
    setName,
    setBirthday,
    setNationality,
    setAcceptSocialTerms,
    setOptOutSocialTerms,
    setNewsletterOptin,
    getNewsletterOptin,
    setSessionCookies,
    refreshSession,
    clearSession,
    initializeLightweightSession,
  };
});
